import React, { useState, useEffect, useMemo } from 'react';
import { 
  getAnaliseIndividual, 
  getAnaliseIndividualRegioes, 
  getAnaliseIndividualEquipes 
} from '../api/dashboard';
import GraficoBarras from './GraficoBarras';

const CARD_STYLE = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  padding: '16px',
  minWidth: '140px',
  flex: '1 1 140px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const CARD_PRINCIPAL_STYLE = {
  ...CARD_STYLE,
  border: '2px solid var(--amber)',
  background: 'rgba(245,158,11,0.05)',
  minWidth: '180px',
  flex: '1 1 180px',
};

const LABEL_STYLE = {
  fontSize: 'var(--font-sm)',
  color: 'var(--muted)',
  fontWeight: 500,
};

const VALUE_STYLE = {
  fontSize: 'var(--font-xl)',
  fontWeight: 700,
  color: 'var(--text)',
  fontFamily: 'var(--font-family)',
};

const VALUE_PRINCIPAL_STYLE = {
  ...VALUE_STYLE,
  fontSize: 'var(--font-2xl)',
  color: 'var(--amber)',
};

const CARD_SECONDARY_STYLE = {
  ...CARD_STYLE,
  flex: '1 1 140px',
};

const CARD_SECONDARY_VALUE_STYLE = {
  ...VALUE_STYLE,
  fontSize: 'var(--font-lg)',
};

const FILTRO_STYLE = {
  display: 'flex',
  gap: '12px',
  alignItems: 'flex-end',
  flexWrap: 'wrap',
  marginBottom: '24px',
  padding: '16px',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
};

const SELECT_WRAPPER = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  minWidth: '180px',
};

const LABEL_FILTRO = {
  fontSize: 'var(--font-sm)',
  color: 'var(--muted)',
  fontWeight: 500,
};

const SELECT_STYLE = {
  padding: '8px 12px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg)',
  color: 'var(--text)',
  fontSize: 'var(--font-base)',
  fontFamily: 'var(--font-family)',
  cursor: 'pointer',
  minWidth: '100%',
};

const SECTION_TITLE_STYLE = {
  fontSize: 'var(--font-lg)',
  fontWeight: 600,
  color: 'var(--text)',
  marginBottom: '16px',
};

const GRID_CARDS = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap: '16px',
  marginBottom: '24px',
};

export default function AbaAnaliseIndividual({ competencia, competencias }) {
  const [carregando, setCarregando] = useState(true);
  const [dados, setDados] = useState(null);
  const [regioes, setRegioes] = useState([]);
  const [equipes, setEquipes] = useState([]);
  const [regiaoSelecionada, setRegiaoSelecionada] = useState(null);
  const [equipeSelecionada, setEquipeSelecionada] = useState(null);

  // Carregar regiões disponíveis
  useEffect(() => {
    if (!competencia) return;
    getAnaliseIndividualRegioes(competencia)
      .then(lista => setRegioes(lista.map(r => r.regiao)))
      .catch(err => console.error('Erro ao carregar regiões:', err));
  }, [competencia]);

  // Carregar equipes quando região muda
  useEffect(() => {
    if (!competencia || !regiaoSelecionada) {
      setEquipes([]);
      setEquipeSelecionada(null);
      return;
    }
    getAnaliseIndividualEquipes(competencia, regiaoSelecionada)
      .then(lista => setEquipes(lista))
      .catch(err => console.error('Erro ao carregar equipes:', err));
    setEquipeSelecionada(null);
  }, [competencia, regiaoSelecionada]);

  // Carregar dados principais
  useEffect(() => {
    if (!competencia) return;
    setCarregando(true);
    getAnaliseIndividual(competencia, regiaoSelecionada, equipeSelecionada)
      .then(setDados)
      .catch(err => console.error('Erro ao carregar análise individual:', err))
      .finally(() => setCarregando(false));
  }, [competencia, regiaoSelecionada, equipeSelecionada]);

  const formatUS = (v) => (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatInt = (v) => (v || 0).toLocaleString('pt-BR');
  const formatNum = (v) => (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Preparar dados para o gráfico
  const chartData = useMemo(() => {
    if (!dados?.serie_diaria || dados.serie_diaria.length === 0) return [];
    return dados.serie_diaria.map((d, i) => ({
      dia: d.data_real,
      us: d.us_dia,
      servicos: d.servicos_dia,
      horas: d.horas_dia,
    }));
  }, [dados?.serie_diaria]);

  if (carregando && !dados) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: 'var(--muted)' }}>
        Carregando análise individual...
      </div>
    );
  }

  if (!dados) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: 'var(--muted)' }}>
        Sem dados para a competência selecionada.
      </div>
    );
  }

  const { cards, filtros_aplicados } = dados;

  return (
    <div style={{ width: '100%' }}>
      {/* Título da seção */}
      <h2 style={SECTION_TITLE_STYLE}>
        Análise Individual — {competencias.find(c => c.id === String(competencia))?.nome || competencia}
      </h2>

      {/* Filtros */}
      <div style={FILTRO_STYLE}>
        <div style={SELECT_WRAPPER}>
          <label style={LABEL_FILTRO}>Região</label>
          <select
            value={regiaoSelecionada || ''}
            onChange={(e) => setRegiaoSelecionada(e.target.value || null)}
            style={SELECT_STYLE}
            disabled={!competencia}
          >
            <option value="">Todas</option>
            <option value="AL">AL</option>
            <option value="RA">RA</option>
          </select>
        </div>

        <div style={SELECT_WRAPPER}>
          <label style={LABEL_FILTRO}>Equipe</label>
          <select
            value={equipeSelecionada || ''}
            onChange={(e) => setEquipeSelecionada(e.target.value ? parseInt(e.target.value) : null)}
            style={SELECT_STYLE}
            disabled={!regiaoSelecionada || equipes.length === 0}
          >
            <option value="">Todas</option>
            {equipes.map(e => (
              <option key={e.numvei} value={e.numvei}>
                {e.numvei} — {e.nomele} ({e.tipo})
              </option>
            ))}
          </select>
        </div>

        {filtros_aplicados?.equipe && (
          <div style={{ 
            fontSize: 'var(--font-sm)', 
            color: 'var(--amber)',
            display: 'flex', 
            alignItems: 'center',
            gap: '8px',
            marginLeft: 'auto'
          }}>
            🔍 Filtrando: Equipe {filtros_aplicados.equipe} ({filtros_aplicados.regiao})
          </div>
        )}
      </div>

      {/* Cards */}
      <div style={GRID_CARDS}>
        {/* Card Principal - Total US */}
        <div style={CARD_PRINCIPAL_STYLE}>
          <div style={LABEL_STYLE}>Total US (Serviços Atendidos)</div>
          <div style={VALUE_PRINCIPAL_STYLE}>{formatUS(cards.total_us)}</div>
        </div>

        {/* Cards Secundários */}
        <div style={CARD_SECONDARY_STYLE}>
          <div style={LABEL_STYLE}>Serviços Atendidos</div>
          <div style={CARD_SECONDARY_VALUE_STYLE}>{formatInt(cards.total_servicos)}</div>
        </div>

        <div style={CARD_SECONDARY_STYLE}>
          <div style={LABEL_STYLE}>Dias Trabalhados</div>
          <div style={CARD_SECONDARY_VALUE_STYLE}>{cards.dias_trabalhados} dias</div>
        </div>

        <div style={CARD_SECONDARY_STYLE}>
          <div style={LABEL_STYLE}>Média US/dia</div>
          <div style={CARD_SECONDARY_VALUE_STYLE}>{formatUS(cards.media_us_dia)}</div>
        </div>

        <div style={CARD_SECONDARY_STYLE}>
          <div style={LABEL_STYLE}>Média Serviços/dia</div>
          <div style={CARD_SECONDARY_VALUE_STYLE}>{formatNum(cards.media_servicos_dia)}</div>
        </div>

        <div style={CARD_SECONDARY_STYLE}>
          <div style={LABEL_STYLE}>Média Horas/dia</div>
          <div style={CARD_SECONDARY_VALUE_STYLE}>{formatNum(cards.media_horas_dia)}h</div>
        </div>

        <div style={CARD_SECONDARY_STYLE}>
          <div style={LABEL_STYLE}>Total Horas</div>
          <div style={CARD_SECONDARY_VALUE_STYLE}>{formatNum(cards.total_horas)}h</div>
        </div>
      </div>

      {/* Gráfico */}
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: '20px',
        marginTop: '8px',
      }}>
        <h3 style={{ ...SECTION_TITLE_STYLE, marginBottom: '8px' }}>US por Dia — Serviços Atendidos</h3>
        <p style={{ fontSize: 'var(--font-sm)', color: 'var(--muted)', marginBottom: '16px' }}>
          Barras representam US de serviços executados no dia (apropriacao_tempo_turma). Não inclui sobreaviso nem ajustes.
        </p>
        <GraficoBarras
          data={chartData}
          labelKey="dia"
          valueKey="us"
          layout="vertical"
          height={320}
          tooltipKeys={['servicos', 'horas']}
          tooltipLabels={{ servicos: 'Serviços', horas: 'Horas' }}
          formatter={formatUS}
        />
      </div>
    </div>
  );
}